import { useCallback, useEffect, useRef, useState } from "react";
import { fetchLoginSession, getGenericLogger } from "src/authentication-and-logging/authAndLogger";
import { useB2CConfig, useSetUserName, useSetUserId } from "../../../datahandling/hooks";

const logger = getGenericLogger().createCategoryLogger("Authorization");

export interface AuthorizationState {
	isAuthorized: boolean;
	isAuthLoading: boolean;
}

/**
 * Hook that manages user authorization and session tokens.
 * Fetches and validates login sessions, sets user info, and tracks auth state.
 *
 * @returns {{ isAuthorized: boolean; isAuthLoading: boolean }} - Authorization state
 */
export function useAuthorization(): AuthorizationState {
	const [isAuthorized, setIsAuthorized] = useState(false);
	const [isAuthLoading, setIsLoading] = useState(true);

	const lastTokenRef = useRef<string | null>(null);
	const b2cConfig = useB2CConfig();
	const setUserName = useSetUserName();
	const setUserId = useSetUserId();

	const authChecker = useCallback(async () => {
		if (!b2cConfig) return;

		try {
			const result = await fetchLoginSession(b2cConfig);

			if (!result) {
				setIsLoading(false);
				return;
			}

			if (!result.expires_on) {
				logger.error("No expires_on in login session");
				setIsLoading(false);
				return;
			}

			// Only update user info if token changed
			if (result.access_token !== lastTokenRef.current) {
				lastTokenRef.current = result.access_token;
				setUserName(result.name ?? "");
				setUserId(result.userId ?? "");
			}

			setIsAuthorized(true);
			setIsLoading(false);
		} catch (error) {
			logger.error("Authorization failed", { error });
			setIsLoading(false);
		}
	}, [b2cConfig, setUserName, setUserId]);

	useEffect(() => {
		authChecker();
	}, [authChecker]);

	return { isAuthorized, isAuthLoading };
}
